import type { CostsToDate } from '../../../../api/initiative-client/initiative-client.type';
import type { Team } from '../../../../api/projects-client/projects-client.type';
import type { Epic } from '../../../../api/tasks-client/task-client.type';
import { graphChefchaouenBlue } from '../../../../styles/design-tokens';
import type { ChartDataItem } from './cost-tab.type';

/**
 * Calculates the percentage of a value given a numerator and denominator.
 * Returns the result rounded to 2 decimal places.
 *
 * @param {number} numerator - The number to calculate the percentage of.
 * @param {number} denominator - The total value to calculate the percentage against.
 * @returns {number} - The percentage of the numerator to the denominator, or 0 if denominator is 0.
 */
const getPercentage = (numerator: number, denominator: number): number => {
  if (denominator === 0) {
    return 0;
  }

  return Number(((numerator / denominator) * 100).toFixed(2));
};

/**
 * Generate tree map data for epics based on costs_to_date
 *
 * @param {CostsToDate} costs - The costs to date information containing epic cost data
 * @param {Epic[]} epics - Array of epics to match with cost data
 * @returns {ChartDataItem[] | []} Array of chart data items for the tree map visualization or empty array if no data
 */
const generateEpicsTreeMapData = (costs: CostsToDate, epics: Epic[]): ChartDataItem[] | [] => {
  if (!costs.epics || Object.keys(costs.epics).length === 0) {
    return [];
  }

  const totalCost = Object.values(costs.epics).reduce((acc, cost) => acc + cost, 0);

  return Object.entries(costs.epics)
    .map(([epicId, cost]) => {
      const epic = epics.find((e) => e.id === epicId);

      if (!epic) return null;

      return {
        id: epicId,
        title: epic.title,
        name: epic.name,
        size: cost,
        percentage: getPercentage(cost, totalCost),
        fill: graphChefchaouenBlue,
      };
    })
    .filter(Boolean) as ChartDataItem[];
};

/**
 * Generate tree map data for teams based on costs_to_date
 *
 * @param {CostsToDate} costs - The costs to date information containing team cost data
 * @param {Team[]} teams - Array of teams to match with cost data
 * @returns {ChartDataItem[] | []} Array of chart data items for the tree map visualization or empty array if no data
 */
const generateTeamsTreeMapData = (costs: CostsToDate, teams: Team[]): ChartDataItem[] | [] => {
  if (!costs.teams || Object.keys(costs.teams).length === 0) {
    return [];
  }

  const totalCost = Object.values(costs.teams).reduce((acc, cost) => acc + cost, 0);

  return Object.entries(costs.teams)
    .map(([teamId, cost]) => {
      const team = teams.find((t) => t.id === teamId);

      if (!team) return null;

      return {
        id: teamId,
        title: team.name,
        size: cost,
        percentage: getPercentage(cost, totalCost),
        fill: graphChefchaouenBlue,
      };
    })
    .filter(Boolean) as ChartDataItem[];
};

export { generateEpicsTreeMapData, generateTeamsTreeMapData };
